import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import * as Icons from "lucide-react";
import { RULE_ICONS, RuleIcon } from "@/components/site/rules/RuleIcon";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { RulesSync } from "@/components/admin/RulesSync";
import { Field, TextArea, TextInput } from "@/components/admin/Field";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

function CategoryDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);
  if (!form) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { title: form.title, description: form.description, icon: form.icon || "", published: form.published };
      if (form.id) await api.put(`/admin/categories/${form.id}`, body);
      else await api.post("/admin/categories", body);
      toast.success(form.id ? "Categoria atualizada." : "Categoria criada.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-eden-border bg-eden-surface text-eden-text" data-testid="category-dialog">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Editar categoria" : "Nova categoria"}</DialogTitle><DialogDescription className="text-eden-muted">Agrupa regras relacionadas.</DialogDescription></DialogHeader>
          <Field label="Título" id="cat-title"><TextInput id="cat-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required maxLength={100} /></Field>
          <Field label="Descrição (opcional)" id="cat-description"><TextArea id="cat-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={2} maxLength={400} /></Field>
          <Field label="Ícone do tema" id="cat-icon" hint="Sem escolha, o site sugere um ícone pelo nome do tema.">
            <div className="grid grid-cols-6 gap-2 sm:grid-cols-10" role="radiogroup" aria-label="Ícone do tema" data-testid="cat-icon-grid">
              <button type="button" role="radio" aria-checked={!form.icon} onClick={() => setForm({ ...form, icon: "" })} className={`grid h-10 place-items-center rounded-sm border text-[10px] font-mono ${!form.icon ? "border-eden-orange bg-eden-orange/10 text-eden-orange" : "border-eden-border text-eden-muted hover:border-eden-secondary"}`} title="Automático" data-testid="cat-icon-auto">auto</button>
              {RULE_ICONS.map(([name, label]) => {
                const Icon = Icons[name];
                const on = form.icon === name;
                return <button key={name} type="button" role="radio" aria-checked={on} onClick={() => setForm({ ...form, icon: name })} className={`grid h-10 place-items-center rounded-sm border ${on ? "border-eden-orange bg-eden-orange/10 text-eden-orange" : "border-eden-border text-eden-secondary hover:border-eden-secondary hover:text-eden-text"}`} title={label} aria-label={label} data-testid={`cat-icon-${name}`}><Icon size={16} /></button>;
              })}
            </div>
          </Field>
          <label className="flex items-center gap-3 text-sm"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="cat-published-switch" /> Publicada no site</label>
          <DialogFooter><button type="submit" className="btn-primary h-10" data-testid="category-dialog-save">Guardar</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RuleDialog({ open, onClose, initial, categories, onSaved }) {
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);
  if (!form) return null;
  const submit = async (e) => {
    e.preventDefault();
    const payload = { category_id: form.category_id, group: form.group || "", title: form.title || "", body: form.body || "", published: form.published };
    try {
      if (form.id) await api.put(`/admin/rules/${form.id}`, payload);
      else await api.post("/admin/rules", payload);
      toast.success(form.id ? "Regra atualizada." : "Regra criada.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-eden-border bg-eden-surface text-eden-text" data-testid="rule-dialog">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Editar regra" : "Nova regra"}</DialogTitle><DialogDescription className="text-eden-muted">O texto aparece exatamente como o escreveres.</DialogDescription></DialogHeader>
          <Field label="Categoria" id="rule-category">
            <select id="rule-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="input-eden" data-testid="select-rule-category">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Subtítulo / grupo (opcional)" id="rule-group" hint="Ex.: Cooldowns. Agrupa regras dentro da categoria."><TextInput id="rule-group" value={form.group} onChange={(v) => setForm({ ...form, group: v })} maxLength={120} /></Field>
            <Field label="Título (opcional)" id="rule-title" hint="Deixa vazio para regras apenas numeradas."><TextInput id="rule-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} maxLength={200} /></Field>
          </div>
          <Field label="Texto da regra" id="rule-body"><TextArea id="rule-body" value={form.body} onChange={(v) => setForm({ ...form, body: v })} rows={6} maxLength={4000} /></Field>
          <label className="flex items-center gap-3 text-sm"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="rule-published-switch" /> Publicada no site</label>
          <DialogFooter><button type="submit" className="btn-primary h-10" data-testid="rule-dialog-save">Guardar</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function EditRegras() {
  const [data, setData] = useState({ categories: [], rules: [], updated_at: null });
  const [catForm, setCatForm] = useState(null);
  const [ruleForm, setRuleForm] = useState(null);
  const [intro, setIntro] = useState("");

  const load = useCallback(() => api.get("/admin/rules").then(({ data }) => { setData(data); setIntro(data.intro || ""); }).catch(() => toast.error("Não foi possível carregar as regras.")), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); await load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  const moveCat = (i, dir) => {
    const ids = data.categories.map((c) => c.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => api.put("/admin/categories/reorder", { ids }));
  };
  const moveRule = (catRules, i, dir) => {
    const ids = catRules.map((r) => r.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => api.put("/admin/rules/reorder", { ids }));
  };

  return (
    <div className="space-y-6" data-testid="rules-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Regras</h2>
          <p className="text-sm text-eden-muted" data-testid="rules-updated-at">As alterações às regras ficam visíveis no site imediatamente. Última alteração: {data.updated_at ? formatDate(data.updated_at) : "—"}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary h-10" onClick={() => setCatForm({ title: "", description: "", icon: "", published: true })} data-testid="add-category-button"><Plus size={16} /> Categoria</button>
          <button type="button" className="btn-primary h-10" disabled={!data.categories.length} onClick={() => setRuleForm({ category_id: data.categories[0]?.id, group: "", title: "", body: "", published: true })} data-testid="add-rule-button"><Plus size={16} /> Regra</button>
        </div>
      </div>

      <RulesSync key={`${data.doc_url || "sync"}-${data.last_auto_sync?.finished_at || ""}-${data.blocked_sync?.at || ""}`} initialUrl={data.doc_url} lastSyncAt={data.last_sync_at} autoSync={data.auto_sync} lastAutoSync={data.last_auto_sync} blockedSync={data.blocked_sync} onSynced={load} />

      <section className="card-eden p-5" data-testid="rules-intro-editor">
        <Field label="Texto introdutório da página de regras" id="rules-intro">
          <TextArea id="rules-intro" value={intro} onChange={setIntro} rows={2} maxLength={1000} />
        </Field>
        <button type="button" className="btn-secondary mt-3 h-9" disabled={intro === (data.intro || "")} onClick={() => act(() => api.put("/admin/rules/intro", { intro }), "Introdução guardada.")} data-testid="rules-intro-save">Guardar introdução</button>
      </section>

      {data.categories.map((c, ci) => {
        const rules = data.rules.filter((r) => r.category_id === c.id);
        return (
          <section key={c.id} className="card-eden" data-testid={`admin-category-${c.id}`}>
            <header className="flex flex-wrap items-center gap-3 border-b border-eden-border px-5 py-4">
              <span className="grid h-8 w-8 place-items-center rounded-sm bg-eden-elevated text-eden-orange" data-testid={`admin-cat-icon-${c.id}`}><RuleIcon category={c} size={16} /></span>
              <h3 className="flex-1 font-display text-lg font-semibold">{c.title} {!c.published && <span className="ml-2 rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">rascunho</span>}</h3>
              <div className="flex gap-1">
                <button type="button" className="btn-ghost h-8 px-2" onClick={() => moveCat(ci, -1)} disabled={ci === 0} aria-label="Mover categoria para cima" data-testid={`cat-up-${c.id}`}><ArrowUp size={14} /></button>
                <button type="button" className="btn-ghost h-8 px-2" onClick={() => moveCat(ci, 1)} disabled={ci === data.categories.length - 1} aria-label="Mover categoria para baixo" data-testid={`cat-down-${c.id}`}><ArrowDown size={14} /></button>
                <button type="button" className="btn-ghost h-8 px-2" onClick={() => setCatForm(c)} aria-label="Editar categoria" data-testid={`cat-edit-${c.id}`}><Pencil size={14} /></button>
                <ConfirmDelete title="Eliminar categoria?" description={`"${c.title}" e as suas ${rules.length} regras serão eliminadas definitivamente.`} onConfirm={() => act(() => api.delete(`/admin/categories/${c.id}`), "Categoria eliminada.")} testId={`cat-delete-${c.id}`}>
                  <button type="button" className="btn-ghost h-8 px-2 text-red-400 hover:text-red-300" aria-label="Eliminar categoria" data-testid={`cat-delete-${c.id}`}><Trash2 size={14} /></button>
                </ConfirmDelete>
              </div>
            </header>
            <ol className="divide-y divide-eden-borderSubtle">
              {rules.length === 0 && <li className="px-5 py-4 text-sm text-eden-muted">Sem regras. Usa o botão “Regra” para adicionar.</li>}
              {rules.map((r, ri) => (
                <li key={r.id} className="flex gap-4 px-5 py-4" data-testid={`admin-rule-${r.id}`}>
                  <span className="pt-0.5 font-mono text-sm text-eden-orange">{ci + 1}.{ri + 1}</span>
                  <div className="min-w-0 flex-1">
                    {r.group && r.group !== rules[ri - 1]?.group && <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-eden-turquoise">{r.group}</p>}
                    {r.title && <p className="font-medium text-eden-text">{r.title} {!r.published && <span className="ml-2 rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">rascunho</span>}</p>}
                    <p className={`line-clamp-2 text-sm ${r.title ? "mt-1 text-eden-secondary" : "text-eden-text"}`}>{r.body} {!r.title && !r.published && <span className="ml-2 rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">rascunho</span>}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button type="button" className="btn-ghost h-8 px-2" onClick={() => moveRule(rules, ri, -1)} disabled={ri === 0} aria-label="Mover regra para cima" data-testid={`rule-up-${r.id}`}><ArrowUp size={14} /></button>
                    <button type="button" className="btn-ghost h-8 px-2" onClick={() => moveRule(rules, ri, 1)} disabled={ri === rules.length - 1} aria-label="Mover regra para baixo" data-testid={`rule-down-${r.id}`}><ArrowDown size={14} /></button>
                    <button type="button" className="btn-ghost h-8 px-2" onClick={() => setRuleForm(r)} aria-label="Editar regra" data-testid={`rule-edit-${r.id}`}><Pencil size={14} /></button>
                    <ConfirmDelete title="Eliminar regra?" description={`A regra ${ci + 1}.${ri + 1} será eliminada definitivamente.`} onConfirm={() => act(() => api.delete(`/admin/rules/${r.id}`), "Regra eliminada.")} testId={`rule-delete-${r.id}`}>
                      <button type="button" className="btn-ghost h-8 px-2 text-red-400 hover:text-red-300" aria-label="Eliminar regra" data-testid={`rule-delete-${r.id}`}><Trash2 size={14} /></button>
                    </ConfirmDelete>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        );
      })}

      <CategoryDialog open={!!catForm} onClose={() => setCatForm(null)} initial={catForm} onSaved={load} />
      <RuleDialog open={!!ruleForm} onClose={() => setRuleForm(null)} initial={ruleForm} categories={data.categories} onSaved={load} />
    </div>
  );
}
